import { z } from 'zod';
import {
  AuditSchema,
  InstantSchema,
  MoneySchema,
  OrderIdSchema,
  OrderLineIdSchema,
  PartyIdSchema,
  PlainDateSchema,
  ProductIdSchema,
  QuantitySchema,
  TenantScopedSchema,
  TimeWindowSchema,
  UserIdSchema,
  addMoney,
  mulMoney,
  zeroMoney,
} from './primitives.js';

/* ------------------------------------------------------------------ *
 * Order status
 *
 * An explicit machine, not a free string. Two things fall out of it:
 * the UI can render only the transitions that are legal, and a year
 * from now a new state is added here and every screen updates.
 * ------------------------------------------------------------------ */

export const OrderStatusSchema = z.enum([
  'draft',      // being typed, not visible to picking
  'placed',     // customer confirmed, inside cutoff
  'sourcing',   // on a shopping list, buyer has it
  'picking',    // warehouse is assembling it
  'ready',      // staged, waiting for a route
  'dispatched', // on a vehicle
  'delivered',  // signed for
  'invoiced',   // document issued
  'cancelled',
]);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

export const ORDER_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  draft: ['placed', 'cancelled'],
  placed: ['sourcing', 'picking', 'cancelled'],
  sourcing: ['picking', 'placed', 'cancelled'],
  picking: ['ready', 'sourcing', 'cancelled'],
  ready: ['dispatched', 'picking', 'cancelled'],
  dispatched: ['delivered', 'ready'],
  delivered: ['invoiced'],
  invoiced: [],
  cancelled: [],
};

export const canTransition = (from: OrderStatus, to: OrderStatus): boolean =>
  ORDER_TRANSITIONS[from].includes(to);

/** Statuses where the order still consumes stock and shows on the board. */
export const OPEN_ORDER_STATUSES: readonly OrderStatus[] = [
  'placed', 'sourcing', 'picking', 'ready', 'dispatched',
];

/* ------------------------------------------------------------------ *
 * Order line
 *
 * The line carries three quantities, and conflating them is the single
 * most expensive modelling mistake available here:
 *
 *   ordered   what the customer asked for
 *   picked    what actually went in the crate (short delivery lives here)
 *   invoiced  what we charge — differs from picked for catch weight
 * ------------------------------------------------------------------ */

export const SubstitutionSchema = z.object({
  /** What we gave instead. */
  productId: ProductIdSchema,
  quantity: QuantitySchema,
  unitPrice: MoneySchema,
  /** Substitutions need a human decision recorded; disputes always come back to this. */
  approvedBy: z.enum(['customer', 'sales', 'auto-rule']),
  approvedAt: InstantSchema,
  note: z.string().nullable().default(null),
});
export type Substitution = z.infer<typeof SubstitutionSchema>;

export const ShortfallReasonSchema = z.enum([
  'out-of-stock',
  'quality-rejected',   // arrived, but not good enough to send
  'supplier-short',     // the market did not have it this morning
  'price-refused',      // customer declined today's price
  'damaged',
  'other',
]);
export type ShortfallReason = z.infer<typeof ShortfallReasonSchema>;

export const OrderLineSchema = z.object({
  id: OrderLineIdSchema,
  productId: ProductIdSchema,
  /** Denormalised at write time. An invoice must not change when a product is renamed. */
  productName: z.string().min(1),
  sku: z.string().min(1),

  ordered: QuantitySchema,
  picked: QuantitySchema.nullable().default(null),
  invoiced: QuantitySchema.nullable().default(null),

  unitPrice: MoneySchema,
  /** Where the price came from, kept for the "why is this cheaper" conversation. */
  priceSource: z.enum(['personal', 'price-list', 'market', 'base', 'manual']),
  discountBps: z.number().int().min(0).max(10_000).default(0),
  taxRateKey: z.string().min(1),

  /** Who covers this line. Set on the grouping board; when null the
      product's default supplier is the working answer. Denormalised
      name for the same reason as `productName`. */
  supplierId: PartyIdSchema.nullable().default(null),
  supplierName: z.string().nullable().default(null),

  shortfall: z
    .object({
      quantity: QuantitySchema,
      reason: ShortfallReasonSchema,
      note: z.string().nullable().default(null),
      recordedBy: UserIdSchema.nullable().default(null),
      recordedAt: InstantSchema,
    })
    .nullable()
    .default(null),
  substitution: SubstitutionSchema.nullable().default(null),

  /** Free text from the customer: "green ones, not ripe". Drivers and pickers read it. */
  note: z.string().nullable().default(null),
  sortOrder: z.number().int().default(0),
});
export type OrderLine = z.infer<typeof OrderLineSchema>;

/* ------------------------------------------------------------------ *
 * Order
 * ------------------------------------------------------------------ */

export const OrderChannelSchema = z.enum([
  'portal', 'whatsapp', 'telegram', 'phone', 'email', 'sales-app', 'recurring',
]);
export type OrderChannel = z.infer<typeof OrderChannelSchema>;

export const OrderSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: OrderIdSchema,
  /** Human reference on every document and in every WhatsApp thread. */
  number: z.string().min(1),
  status: OrderStatusSchema,
  customerId: PartyIdSchema,
  customerName: z.string().min(1),

  /** How it arrived. Half of B2B food ordering is a voice note; say so. */
  channel: OrderChannelSchema,
  placedAt: InstantSchema.nullable().default(null),

  /** Calendar day in the business timezone. */
  deliveryDate: PlainDateSchema,
  deliveryWindow: TimeWindowSchema.nullable().default(null),
  deliveryAddressId: z.string().nullable().default(null),

  currency: z.string().length(3),
  lines: z.array(OrderLineSchema),

  /** Applied to the whole document, after line discounts. */
  documentDiscountBps: z.number().int().min(0).max(10_000).default(0),
  deliveryFee: MoneySchema.nullable().default(null),

  assignedToId: UserIdSchema.nullable().default(null),
  shipmentId: z.string().nullable().default(null),
  invoiceId: z.string().nullable().default(null),

  customerNote: z.string().nullable().default(null),
  internalNote: z.string().nullable().default(null),
  tags: z.array(z.string()).default([]),
});
export type Order = z.infer<typeof OrderSchema>;

/* ------------------------------------------------------------------ *
 * Totals
 *
 * Computed in shared, used by list rows, the detail panel, the invoice
 * preview and the API. One implementation or four subtly different
 * numbers — there is no third option.
 * ------------------------------------------------------------------ */

export interface OrderTotals {
  net: import('./primitives.js').Money;
  discount: import('./primitives.js').Money;
  tax: import('./primitives.js').Money;
  gross: import('./primitives.js').Money;
  /** How much of what was ordered actually shipped, 0..1. Null before picking. */
  fillRate: number | null;
  lineCount: number;
  shortLineCount: number;
}

/** The quantity a line is billed on: invoiced ?? picked ?? ordered. */
export const billableQuantity = (line: OrderLine): number =>
  (line.invoiced ?? line.picked ?? line.ordered).value;

export const lineNet = (line: OrderLine): import('./primitives.js').Money => {
  const gross = mulMoney(line.unitPrice, billableQuantity(line));
  return line.discountBps === 0
    ? gross
    : mulMoney(gross, 1 - line.discountBps / 10_000);
};

export const computeOrderTotals = (
  order: Order,
  taxBpsFor: (rateKey: string) => number,
): OrderTotals => {
  const currency = order.currency;
  let net = zeroMoney(currency);
  let tax = zeroMoney(currency);
  let listed = zeroMoney(currency);
  let shortLineCount = 0;
  let orderedUnits = 0;
  let shippedUnits = 0;

  for (const line of order.lines) {
    const lineListed = mulMoney(line.unitPrice, billableQuantity(line));
    const value = lineNet(line);
    listed = addMoney(listed, lineListed);
    net = addMoney(net, value);
    tax = addMoney(tax, mulMoney(value, taxBpsFor(line.taxRateKey) / 10_000));

    if (line.shortfall) shortLineCount += 1;
    orderedUnits += line.ordered.value;
    shippedUnits += line.picked?.value ?? 0;
  }

  if (order.documentDiscountBps > 0) {
    const factor = 1 - order.documentDiscountBps / 10_000;
    net = mulMoney(net, factor);
    tax = mulMoney(tax, factor);
  }

  if (order.deliveryFee) {
    net = addMoney(net, order.deliveryFee);
  }

  const anyPicked = order.lines.some((l) => l.picked !== null);

  return {
    net,
    discount: { minor: listed.minor - net.minor, currency },
    tax,
    gross: addMoney(net, tax),
    fillRate: anyPicked && orderedUnits > 0 ? shippedUnits / orderedUnits : null,
    lineCount: order.lines.length,
    shortLineCount,
  };
};

/* ------------------------------------------------------------------ *
 * Recurring orders
 *
 * A restaurant orders roughly the same thing on the same weekdays.
 * The template generates a draft; a human still confirms it, because
 * auto-placing an order nobody read is how you deliver 40 kg of
 * mussels to a closed kitchen in August.
 * ------------------------------------------------------------------ */

export const OrderTemplateSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: z.string().min(1),
  name: z.string().min(1),
  customerId: PartyIdSchema,
  weekdays: z.array(z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'])).min(1),
  lines: z.array(
    z.object({
      productId: ProductIdSchema,
      quantity: QuantitySchema,
      note: z.string().nullable().default(null),
    }),
  ),
  /** Generate the draft this many days before delivery. */
  leadDays: z.number().int().min(0).default(1),
  /** Pause without deleting — August exists. */
  pausedUntil: PlainDateSchema.nullable().default(null),
  active: z.boolean().default(true),
});
export type OrderTemplate = z.infer<typeof OrderTemplateSchema>;
